import { useAuth0 } from "@auth0/auth0-react";
import { Navigate } from "react-router-dom";
import { message } from "antd";
import React, { createContext, useContext, useEffect, useState } from "react";
import type { IdToken } from "@auth0/auth0-react";
import LoadingComponent from "../components/LoadingComponent";
import {
  create,
  getQuery,
  setAuthorization,
  setLoggedInUser,
} from "../services/apiService";
import {
  useAddLoginHistoryService,
  type LoginHistoryPayload,
} from "../services/userService";
import getDeviceDetails from "../utils/DeviceDetails";

interface AuthUser {
  sub?: string;
  email?: string;
}

interface UserProfile {
  _id?: string;
  id?: string;
  email?: string;
  role?: number;
}

interface UserBusinessFeatures {
  businessId?: string;
  businessName?: string;
  roleKey: string;
  roleName: string;
  features: Array<{
    key: string;
    name: string;
  }>;
}

interface UserFeatureAccess {
  platform: string[];
  administrator: string[];
  businesses: UserBusinessFeatures[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  login: () => Promise<React.ReactNode | void>;
  logout: () => Promise<void>;
  userProfile: UserProfile | null;
  userFeatures: UserFeatureAccess | null;
  authStatus: "loading" | "ready" | "error";
  hasAdminFeature: (featureKey: string) => boolean;
  user?: AuthUser;
}

interface V2Response<T> {
  success: boolean;
  data: T;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const AUTH_RETURN_PATH_STORAGE_KEY = "authReturnTo";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userFeatures, setUserFeatures] = useState<UserFeatureAccess | null>(
    null
  );
  const [authStatus, setAuthStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout: auth0Logout,
  } = useAuth0();
  const { addLoginHistory } = useAddLoginHistoryService();
  const login = async () => {
    setAuthStatus("loading");
    try {
      const audience = process.env.REACT_APP_AUTH_AUDIENCE;
      const tokenResponse = await getAccessTokenSilently({
        authorizationParams: {
          audience, //'https://dev-fu88hpvhk3wk7i2x.us.auth0.com/api/v2/',//*/"https://auth.80eight.io.us.auth0.com/api/v2/",
          scope: "openid profile email",
        },
      });
      const token = typeof tokenResponse === "string" ? tokenResponse : "";

      const idToken = await getIdTokenClaims();
      setLoggedInUser(idToken as IdToken | undefined);
      setAuthorization(token);

      // v2 response: axios wraps in response.data, giving { success: true, data: UserProfile }
      const [profileResponse, featureResponse] = await Promise.all([
        getQuery<V2Response<UserProfile>>("/api/v2/user/me"),
        getQuery<V2Response<UserFeatureAccess>>(
          "/api/v2/user/me/features?platform=web"
        ),
      ]);

      if (profileResponse.status === 200 && profileResponse.data.data) {
        setUserProfile(profileResponse.data.data);
        setUserFeatures(featureResponse.data.data);
        setAuthStatus("ready");

        if (!sessionStorage.getItem("loginHistoryRecorded")) {
          const { ipAddress, device, browser } = await getDeviceDetails();

          const payload: LoginHistoryPayload = {
            ipAddress,
            device,
            browser,
          };

          addLoginHistory(
            payload,
            () => {
              sessionStorage.setItem("loginHistoryRecorded", "true");
            },
            () => {
              message.error("Failed to record login history");
            }
          );
        }
      }
    } catch (_error) {
      setUserProfile(null);
      setUserFeatures(null);
      setAuthStatus("error");
      message.error(`Could not fetch user profile.`);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem("authId", user?.sub || "");
      login();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      sessionStorage.setItem(AUTH_RETURN_PATH_STORAGE_KEY, returnTo);

      void loginWithRedirect({
        appState: {
          returnTo,
        },
        async openUrl(url) {
          const targetUrl = String(url);
          sessionStorage.setItem("authRedirectTarget", targetUrl);
          window.location.replace(targetUrl);
        },
      }).catch(() => {
        message.error("Authentication redirect failed.");
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Show a loading state while the SDK processes authentication
  if (isLoading || !isAuthenticated) {
    return <LoadingComponent />;
  }

  if (authStatus === "error") {
    return <Navigate to="/unauthorized" replace />;
  }

  const logout = async () => {
    try {
      await create("/api/v2/auth/sign-out");
    } catch (error) {
      console.error("Backend sign out failed:", error);
    } finally {
      sessionStorage.clear();
      setAuthorization();
      setIsAuthenticated(false);
      setUserProfile(null);
      setUserFeatures(null);
      setAuthStatus("loading");
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        userProfile,
        userFeatures,
        authStatus,
        hasAdminFeature: (featureKey: string) =>
          userFeatures?.administrator.includes(featureKey) ?? false,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
